import { useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowRight,
    MapPin,
    Stethoscope,
    Lock,
    Activity,
} from "lucide-react";

// STREAMLINED MODE — only InduceXT is exposed publicly. Add product ids to this
// list to bring others back online without changing the data below.
const ACTIVE_PRODUCT_IDS = ["inducext"];
// When true, hide the territory picker + waitlist scaffolding (single-product mode).
const SINGLE_PRODUCT_MODE = true;

const TERRITORIES = [
    {
        id: "chicago-midwest",
        name: "Denver / Rocky Mountain",
        status: "Active — Early Access",
        statusTone: "active",
        categories: "Orthopedics, Biologics, Surgical Wound Care",
        distribution: "Local distributor partnership model in development",
        caseSupport:
            "Designed for scheduled cases, add-on cases, and emergent territory support when available",
        availability:
            "All current product opportunities are available in this territory.",
        cta: "View Denver Opportunities",
        href: "#chicago-opportunities",
        isScroll: true,
    },
    {
        id: "texas",
        name: "Texas",
        status: "Coming Soon",
        statusTone: "soon",
        categories: "Orthopedics, Biologics, Surgical Wound Care",
        distribution: "Seeking distributor partners",
        caseSupport: "Pending local partner coverage",
        availability:
            "No active product cards yet. Join the territory waitlist.",
        cta: "Join Territory Waitlist",
        href: "/contact?territory=Texas",
    },
    {
        id: "florida",
        name: "Florida",
        status: "Coming Soon",
        statusTone: "soon",
        categories: "Orthopedics, Biologics, Surgical Wound Care",
        distribution: "Seeking distributor partners",
        caseSupport: "Pending local partner coverage",
        availability:
            "No active product cards yet. Join the territory waitlist.",
        cta: "Join Territory Waitlist",
        href: "/contact?territory=Florida",
    },
    {
        id: "northeast",
        name: "Northeast",
        status: "Coming Soon",
        statusTone: "soon",
        categories: "Orthopedics, Biologics, Surgical Wound Care",
        distribution: "Seeking distributor partners",
        caseSupport: "Pending local partner coverage",
        availability:
            "No active product cards yet. Join the territory waitlist.",
        cta: "Join Territory Waitlist",
        href: "/contact?territory=Northeast",
    },
    {
        id: "southeast",
        name: "Southeast",
        status: "Coming Soon",
        statusTone: "soon",
        categories: "Orthopedics, Biologics, Surgical Wound Care",
        distribution: "Seeking distributor partners",
        caseSupport: "Pending local partner coverage",
        availability:
            "No active product cards yet. Join the territory waitlist.",
        cta: "Join Territory Waitlist",
        href: "/contact?territory=Southeast",
    },
    {
        id: "national",
        name: "National Opportunities",
        status: "Selective Opportunities",
        statusTone: "selective",
        categories:
            "Products that do not require local case support or can be supported remotely",
        distribution: "Varies by product and territory",
        caseSupport: "Product dependent",
        availability:
            "No active product cards yet. Submit interest for future national opportunities.",
        cta: "Submit Interest",
        href: "/contact?territory=National",
    },
];

const CHICAGO_PRODUCTS = [
    {
        id: "cerament-g",
        name: "CERAMENT G",
        type: "biologics",
        interest: "Orthopedics",
        category:
            "Orthopedic bone void filler / infection-focused orthopedic product",
        distributorPartner: null,
        caseSupport: "Sterile boxed",
        commission: "Disclosed upon qualified rep introduction",
        infoUrl: "https://www.bonesupport.com/en-eu/products/cerament-g/",
        description:
            "CERAMENT G is positioned for orthopedic applications involving bone void management, infection-related cases, trauma, revision, and limb-salvage workflows.",
        repTypes: [
            "Orthopedic trauma reps",
            "Foot & ankle reps",
            "Limb salvage reps",
            "Ortho biologics reps",
            "Hospital-based orthopedic reps",
            "Reps with access to infection, revision, or complex reconstruction surgeons",
        ],
        callPoints: [
            "Orthopedic trauma",
            "Foot & ankle",
            "Limb salvage",
            "Revision orthopedics",
            "Infection-focused orthopedic surgeons",
            "Hospital ORs",
        ],
        cta: "Rep CERAMENT G in Chicago",
    },
    {
        id: "celleraterx",
        name: "CellerateRX",
        type: "biologics",
        interest: "Wound Care",
        category:
            "Surgical wound care / collagen-based wound management product",
        distributorPartner: null,
        caseSupport: "Sterile boxed",
        commission: "Disclosed upon qualified rep introduction",
        infoUrl: "/brochures/cellerate-rx-brochure.pdf",
        description:
            "CellerateRX is positioned for surgical wound management, soft-tissue healing support, high-risk closures, traumatic wounds, and wound-care workflows.",
        repTypes: [
            "Biologics reps",
            "Wound care reps",
            "Podiatry reps",
            "Plastic surgery reps",
            "General surgery reps",
            "Orthopedic trauma reps with wound-care access",
            "Reps calling on ASCs, clinics, and hospital ORs",
        ],
        callPoints: [
            "Wound care",
            "Podiatry",
            "Plastic surgery",
            "General surgery",
            "Orthopedic trauma",
            "ASC and outpatient surgery settings",
            "High-risk surgical closure workflows",
        ],
        cta: "Rep CellerateRX in Chicago",
    },
    {
        id: "inducext",
        name: "InduceXT",
        type: "biologics",
        interest: "Orthopedics",
        category: "Orthopedic biologic / regenerative tissue product",
        distributorPartner: null,
        caseSupport: "Sterile boxed",
        commission: "Disclosed upon qualified rep introduction",
        infoUrl: "/brochures/inducext-brochure.pdf",
        description:
            "InduceXT is positioned for orthopedic and biologics workflows involving regenerative tissue applications, soft-tissue support, and supplemental case use alongside primary orthopedic and wound-care procedures.",
        repTypes: [
            "Orthopedic biologics reps",
            "Orthopedic trauma reps",
            "Foot & ankle reps",
            "Podiatry reps",
            "Wound care reps",
            "Reps with hospital OR and ASC access",
        ],
        callPoints: [
            "Orthopedic biologics",
            "Foot & ankle",
            "Podiatry",
            "Wound care",
            "Hospital ORs",
            "ASC and outpatient surgery settings",
        ],
        cta: "Rep InduceXT in Chicago",
    },
    {
        id: "conventus-flower-fibular-nail",
        name: "Conventus Flower Fibular Nail",
        type: "hardware",
        interest: "Orthopedics",
        category:
            "Orthopedic trauma implant / intramedullary fibular fixation device",
        distributorPartner: null,
        caseSupport: "Sterile boxed",
        commission: "Disclosed upon qualified rep introduction",
        infoUrl: "https://conventusflower.com/flexthread/",
        description:
            "The Conventus Flower Fibular Nail is positioned for orthopedic trauma applications involving fibular fracture fixation, ankle fracture cases, and minimally invasive intramedullary reconstruction workflows.",
        repTypes: [
            "Orthopedic trauma reps",
            "Foot & ankle reps",
            "Podiatry reps",
            "Limb salvage reps",
            "Hospital-based orthopedic reps",
            "Reps with access to fracture, trauma, and reconstruction surgeons",
        ],
        callPoints: [
            "Orthopedic trauma",
            "Foot & ankle",
            "Podiatry",
            "Fracture fixation",
            "Limb salvage",
            "Hospital ORs",
        ],
        cta: "Rep Conventus Flower in Chicago",
    },
    {
        id: "orthocell-remplir",
        name: "Orthocell Remplir",
        type: "biologics",
        interest: "Biologics",
        category:
            "Biologic implant / collagen nerve wrap for peripheral nerve repair",
        distributorPartner: null,
        caseSupport: "Sterile boxed",
        commission: "Disclosed upon qualified rep introduction",
        infoUrl: "https://orthocell.com/remplir/",
        description:
            "Orthocell Remplir is positioned for peripheral nerve repair workflows, including direct nerve coaptation support, upper-extremity and hand cases, and reconstructive procedures where a collagen nerve wrap is indicated.",
        repTypes: [
            "Orthopedic biologics reps",
            "Hand and upper-extremity reps",
            "Plastic surgery reps",
            "Orthopedic trauma reps",
            "Reconstructive surgery reps",
            "Reps with hospital OR and ASC access",
        ],
        callPoints: [
            "Peripheral nerve repair",
            "Hand and upper-extremity",
            "Plastic and reconstructive surgery",
            "Orthopedic trauma",
            "Hospital ORs",
            "ASC and outpatient surgery settings",
        ],
        cta: "Rep Remplir in Chicago",
    },
    {
        id: "fitbone",
        name: "Orthofix Fitbone",
        type: "hardware",
        interest: "Orthopedics",
        category:
            "Orthopedic implant / motorized intramedullary limb-lengthening nail",
        distributorPartner: null,
        caseSupport: "Sterile boxed",
        commission: "Disclosed upon qualified rep introduction",
        infoUrl: "https://www.orthofix.com/products/limb-reconstruction/fitbone/",
        description:
            "Fitbone is positioned for orthopedic limb-lengthening and deformity-correction workflows, including femoral and tibial reconstruction cases where a fully implantable motorized intramedullary nail is indicated.",
        repTypes: [
            "Orthopedic reconstruction reps",
            "Orthopedic trauma reps",
            "Limb salvage reps",
            "Pediatric orthopedic reps",
            "Hospital-based orthopedic reps",
            "Reps with access to limb-lengthening and deformity-correction surgeons",
        ],
        callPoints: [
            "Limb lengthening",
            "Deformity correction",
            "Orthopedic trauma",
            "Limb salvage",
            "Pediatric orthopedics",
            "Hospital ORs",
        ],
        cta: "Rep Fitbone in Chicago",
    },
    {
        id: "orthofix-tl-elevate",
        name: "Orthofix TL Elevate",
        type: "hardware",
        interest: "Orthopedics",
        category:
            "Orthopedic reconstruction implant / transverse bone transport system",
        distributorPartner: null,
        caseSupport: "Sterile boxed",
        commission: "Disclosed upon qualified rep introduction",
        infoUrl: "https://orthofix.com/solutions/limb-reconstruction/limb-preservation/truelok-elevate/",
        description:
            "Orthofix TL Elevate is positioned for orthopedic limb-reconstruction workflows involving transverse bone transport, segmental bone defect management, non-union and infected-non-union cases, and limb-salvage procedures.",
        repTypes: [
            "Orthopedic trauma reps",
            "Orthopedic reconstruction reps",
            "Limb salvage reps",
            "Foot & ankle reps",
            "Hospital-based orthopedic reps",
            "Reps with access to trauma, non-union, and limb-reconstruction surgeons",
        ],
        callPoints: [
            "Orthopedic trauma",
            "Limb reconstruction",
            "Transverse bone transport",
            "Non-union and infected non-union",
            "Limb salvage",
            "Hospital ORs",
        ],
        cta: "Rep TL Elevate in Chicago",
    },
    {
        id: "conmed-biobrace",
        name: "Conmed BioBrace",
        type: "biologics",
        interest: "Orthopedics",
        category:
            "Sports medicine implant / bioinductive collagen scaffold for soft-tissue reinforcement",
        distributorPartner: null,
        caseSupport: "Sterile boxed",
        commission: "Disclosed upon qualified rep introduction",
        infoUrl: "https://www.conmed.com/en/products/implants-and-suture-anchors/biobrace/biobrace-implant",
        description:
            "Conmed BioBrace is positioned for sports medicine and soft-tissue reinforcement workflows, including rotator cuff repair, tendon augmentation, and reconstructive procedures where a bioinductive collagen scaffold is indicated.",
        repTypes: [
            "Sports medicine reps",
            "Orthopedic sports med reps",
            "Shoulder and upper-extremity reps",
            "Orthopedic biologics reps",
            "Foot & ankle reps",
            "Reps with ASC and hospital OR access",
        ],
        callPoints: [
            "Sports medicine",
            "Rotator cuff and shoulder repair",
            "Tendon and soft-tissue augmentation",
            "Foot & ankle",
            "ASC and outpatient surgery settings",
            "Hospital ORs",
        ],
        cta: "Rep BioBrace in Chicago",
    },
];

const WHY_CARDS = [
    {
        id: "local-distribution",
        icon: MapPin,
        title: "Local distribution support",
        body: "Product opportunities are stronger when there is a local distributor or territory partner who can support logistics, inventory, and case needs.",
    },
    {
        id: "add-on-cases",
        icon: Activity,
        title: "Add-on and emergent cases",
        body: "Orthopedic and wound-care opportunities may require responsive local coverage for add-on, urgent, or hospital-based cases.",
    },
    {
        id: "private-interest",
        icon: Lock,
        title: "Private rep interest",
        body: "Reps can express interest privately. Introductions happen only after 1099er reviews fit.",
    },
];

function StatusPill({ tone, children, testId }) {
    const toneClass =
        tone === "active"
            ? "bg-slate-900 text-white border-slate-900"
            : tone === "selective"
            ? "bg-white text-slate-900 border-slate-900"
            : "bg-[#FAFAFA] text-slate-600 border-slate-200";
    return (
        <span
            data-testid={testId}
            className={`inline-flex items-center text-[11px] font-medium uppercase tracking-[0.14em] px-2.5 py-1 rounded-full border ${toneClass}`}
        >
            {children}
        </span>
    );
}

function MetaRow({ label, value, testId }) {
    return (
        <div className="grid grid-cols-[8rem_1fr] gap-3 text-sm leading-relaxed">
            <span className="text-slate-500">{label}</span>
            <span data-testid={testId} className="text-slate-900">
                {value}
            </span>
        </div>
    );
}

function ProductCard({ p }) {
    return (
        <article
            data-testid={`product-card-${p.id}`}
            className="group flex flex-col border border-slate-200 bg-white rounded-lg p-7 md:p-9 hover:border-slate-900 transition-colors"
        >
            <span
                data-testid={`product-category-${p.id}`}
                className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500"
            >
                {p.category}
            </span>
            <h3
                data-testid={`product-name-${p.id}`}
                className="font-display mt-5 text-2xl md:text-3xl font-semibold tracking-tight text-slate-900"
            >
                {p.name}
            </h3>

            <div className="mt-7 space-y-2.5">
                <MetaRow
                    label="Territory"
                    value="United States"
                    testId={`product-territory-${p.id}`}
                />
                <MetaRow
                    label="Case Support"
                    value={p.caseSupport}
                    testId={`product-case-${p.id}`}
                />
                <MetaRow
                    label="Commission"
                    value={p.commission}
                    testId={`product-commission-${p.id}`}
                />
            </div>

            <p
                data-testid={`product-description-${p.id}`}
                className="mt-7 pt-6 border-t border-slate-100 text-sm md:text-base text-slate-700 leading-relaxed"
            >
                {p.description}
            </p>

            <div className="mt-6">
                <div>
                    <span className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500 flex items-center gap-1.5">
                        <Stethoscope size={12} /> Best-fit rep types
                    </span>
                    <ul
                        data-testid={`product-rep-types-${p.id}`}
                        className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-slate-700"
                    >
                        {p.repTypes.map((r) => (
                            <li
                                key={r}
                                className="flex gap-2 leading-snug"
                            >
                                <span className="text-slate-400 mt-1.5 shrink-0 w-1 h-1 rounded-full bg-slate-400" />
                                <span>{r}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className="mt-9 flex flex-col sm:flex-row gap-3 self-start">
                {p.infoUrl && (
                    p.infoUrl.startsWith("/") ? (
                        <button
                            type="button"
                            onClick={async (e) => {
                                e.preventDefault();
                                try {
                                    const res = await fetch(p.infoUrl);
                                    const blob = await res.blob();
                                    const url = URL.createObjectURL(
                                        new Blob([blob], { type: "application/pdf" })
                                    );
                                    window.open(url, "_blank", "noopener,noreferrer");
                                    setTimeout(() => URL.revokeObjectURL(url), 60000);
                                } catch (err) {
                                    // Fallback: direct navigation
                                    window.open(p.infoUrl, "_blank", "noopener,noreferrer");
                                }
                            }}
                            data-testid={`product-info-btn-${p.id}`}
                            className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 border border-slate-300 text-sm font-medium px-6 py-3 rounded-md hover:border-slate-900 transition-colors"
                        >
                            Information on {p.name}
                            <ArrowRight size={14} />
                        </button>
                    ) : (
                        <a
                            href={p.infoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`product-info-btn-${p.id}`}
                            className="inline-flex items-center justify-center gap-2 bg-white text-slate-900 border border-slate-300 text-sm font-medium px-6 py-3 rounded-md hover:border-slate-900 transition-colors"
                        >
                            Information on {p.name}
                            <ArrowRight size={14} />
                        </a>
                    )
                )}
                <Link
                    to={`/for-reps?product=${encodeURIComponent(
                        p.name
                    )}&interest=${encodeURIComponent(p.interest)}`}
                    data-testid={`product-interested-btn-${p.id}`}
                    className="inline-flex items-center justify-center gap-2 bg-slate-900 text-white text-sm font-medium px-6 py-3 rounded-md hover:bg-slate-800 transition-colors"
                >
                    {p.cta}
                    <ArrowRight size={14} />
                </Link>
            </div>
        </article>
    );
}

export default function ProductsPage() {
    const [selectedTerritoryId, setSelectedTerritoryId] = useState("chicago-midwest");
    const selectedTerritory = TERRITORIES.find((t) => t.id === selectedTerritoryId);

    const chicagoDetail = selectedTerritory && selectedTerritory.id === "chicago-midwest";
    const otherDetail = selectedTerritory && selectedTerritory.id !== "chicago-midwest";

    return (
        <div data-testid="products-page" className="bg-white">
            {/* Hero */}
            <section className="border-b border-slate-200">
                <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-28">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-200 bg-[#FAFAFA] text-xs font-medium text-slate-600">
                            <MapPin size={12} />
                            <span>Territory-Based Opportunities</span>
                        </div>
                        <h1
                            data-testid="products-headline"
                            className="font-display mt-6 text-4xl md:text-6xl font-semibold tracking-tighter text-slate-900 leading-[1.05]"
                        >
                            Expand your bag.
                            <br />
                            <span className="text-slate-500">Product Opportunities.</span>
                        </h1>
                        <p
                            data-testid="products-subhead"
                            className="mt-6 text-base md:text-lg text-slate-600 max-w-2xl leading-relaxed"
                        >
                            Explore orthopedic, biologics, and surgical wound-care
                            product-line opportunities available in select territories
                            with local distribution and case-support pathways.
                        </p>

                        <div className="mt-10 flex flex-col sm:flex-row gap-3">
                            <Link
                                to="/for-reps"
                                data-testid="hero-join-rep-list-btn"
                                className="inline-flex items-center justify-center gap-2 bg-slate-900 text-white text-sm font-medium px-6 py-3 rounded-md hover:bg-slate-800 transition-colors"
                            >
                                Schedule a Confidential Call
                                <ArrowRight size={16} />
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* Territory Detail moved to top — dynamic based on selection */}
            {chicagoDetail && (
            <section
                id="territory-detail"
                data-testid="chicago-opportunities-section"
                className="border-b border-slate-200 bg-[#FAFAFA] scroll-mt-24"
            >
                <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-24">
                    <div className="max-w-3xl">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-900 text-white text-[11px] font-medium uppercase tracking-[0.14em]">
                            <MapPin size={12} />
                            Active Territory
                        </div>
                        <h2
                            data-testid="chicago-headline"
                            className="font-display mt-5 text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 leading-tight"
                        >
                            United States
                            <br />
                            <span className="text-slate-500">
                                Product Opportunities.
                            </span>
                        </h2>
                        <p
                            data-testid="chicago-subhead"
                            className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed"
                        >
                            1099er is partnering with InduceXT nationwide — available
                            to experienced orthopedic, biologics, wound-care, ASC, and
                            hospital OR reps across the United States.
                        </p>
                    </div>

                    <div
                        data-testid="chicago-products-grid"
                        className="mt-8"
                    >
                        {[
                            {
                                key: "biologics",
                                label: "Biologics",
                                subtitle:
                                    "Bone graft substitutes, collagen scaffolds, regenerative and wound-care products.",
                            },
                            {
                                key: "hardware",
                                label: "Hardware",
                                subtitle:
                                    "Implants, intramedullary devices, and reconstruction systems.",
                            },
                        ].map((cat) => {
                            const items = CHICAGO_PRODUCTS.filter(
                                (p) => p.type === cat.key && ACTIVE_PRODUCT_IDS.includes(p.id)
                            );
                            if (items.length === 0) return null;
                            return (
                                <div
                                    key={cat.key}
                                    data-testid={`chicago-category-${cat.key}`}
                                    className="mt-10 first:mt-0"
                                >
                                    <div className="flex items-baseline justify-between flex-wrap gap-3 border-b border-slate-200 pb-4">
                                        <div>
                                            <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500">
                                                Category
                                            </span>
                                            <h3
                                                data-testid={`chicago-category-label-${cat.key}`}
                                                className="font-display mt-1 text-2xl md:text-3xl font-semibold tracking-tight text-slate-900"
                                            >
                                                {cat.label}
                                            </h3>
                                        </div>
                                        <p className="text-sm text-slate-500 max-w-md">
                                            {cat.subtitle}
                                        </p>
                                    </div>
                                    <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-5">
                                        {items.map((p) => (
                                            <ProductCard key={p.id} p={p} />
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>
            )}

            {/* Non-Chicago territories — waitlist / selective view */}
            {otherDetail && (
                <section
                    id="territory-detail"
                    data-testid={`territory-detail-${selectedTerritory.id}`}
                    className="border-b border-slate-200 bg-[#FAFAFA] scroll-mt-24"
                >
                    <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-24">
                        <div className="max-w-3xl">
                            <div
                                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-medium uppercase tracking-[0.14em] ${
                                    selectedTerritory.statusTone === "selective"
                                        ? "bg-white text-slate-900 border border-slate-900"
                                        : "bg-slate-200 text-slate-600"
                                }`}
                            >
                                <MapPin size={12} />
                                {selectedTerritory.status}
                            </div>
                            <h2
                                data-testid="territory-detail-headline"
                                className="font-display mt-5 text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 leading-tight"
                            >
                                {selectedTerritory.name}
                                <br />
                                <span className="text-slate-500">
                                    {selectedTerritory.statusTone === "selective"
                                        ? "Selective Opportunities."
                                        : "Coming Soon."}
                                </span>
                            </h2>
                            <p
                                data-testid="territory-detail-subhead"
                                className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed"
                            >
                                {selectedTerritory.availability}
                            </p>
                        </div>

                        <div
                            data-testid="territory-waitlist-panel"
                            className="mt-12 border border-slate-200 bg-white rounded-lg p-7 md:p-9"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-8 md:items-center">
                                <div className="max-w-2xl">
                                    <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500 flex items-center gap-2">
                                        <MapPin size={12} /> {selectedTerritory.name}
                                    </span>
                                    <h3
                                        className="font-display mt-4 text-2xl md:text-3xl font-semibold tracking-tight text-slate-900 leading-tight"
                                    >
                                        No active product cards yet.
                                    </h3>
                                    <p className="mt-4 text-sm md:text-base text-slate-600 leading-relaxed">
                                        1099er is currently evaluating distributor
                                        partnerships and product-line opportunities in
                                        {" "}{selectedTerritory.name}. Reps with existing
                                        surgeon relationships in this market are
                                        encouraged to join the waitlist — we&rsquo;ll
                                        reach out as opportunities open.
                                    </p>

                                    <dl className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <dt className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">
                                                Categories
                                            </dt>
                                            <dd className="mt-1.5 text-sm text-slate-800">
                                                {selectedTerritory.categories}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-500">
                                                Local Distribution
                                            </dt>
                                            <dd className="mt-1.5 text-sm text-slate-800">
                                                {selectedTerritory.distribution}
                                            </dd>
                                        </div>
                                    </dl>
                                </div>

                                <Link
                                    to="/for-reps"
                                    data-testid="territory-waitlist-cta"
                                    className="inline-flex items-center justify-center gap-2 bg-slate-900 text-white text-sm font-medium px-6 py-3 rounded-md hover:bg-slate-800 transition-colors self-start md:self-auto"
                                >
                                    Join {selectedTerritory.name} Waitlist
                                    <ArrowRight size={14} />
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* Why territory matters */}
            {!SINGLE_PRODUCT_MODE && (
            <section
                data-testid="why-territory-section"
                className="border-b border-slate-200 bg-[#FAFAFA]"
            >
                <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-24">
                    <div className="max-w-3xl">
                        <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500">
                            The Thesis
                        </span>
                        <h2
                            data-testid="why-territory-headline"
                            className="font-display mt-4 text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 leading-tight"
                        >
                            Why territory matters.
                        </h2>
                        <p className="mt-5 text-base md:text-lg text-slate-600 leading-relaxed">
                            In orthopedics, biologics, wound care, trauma, and add-on
                            cases, the product is only part of the sale. Reps also need
                            local support, inventory access, case coverage, training,
                            and a distributor pathway that can handle real hospital,
                            ASC, and outpatient workflows.
                        </p>
                        <p className="mt-4 text-base md:text-lg text-slate-600 leading-relaxed">
                            1099er organizes product-line opportunities by territory so
                            reps can find products that are commercially supportable in
                            their market.
                        </p>
                    </div>

                    <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
                        {WHY_CARDS.map(({ id, icon: Icon, title, body }) => (
                            <div
                                key={id}
                                data-testid={`why-card-${id}`}
                                className="bg-white border border-slate-200 rounded-lg p-7"
                            >
                                <Icon
                                    size={20}
                                    strokeWidth={1.75}
                                    className="text-slate-900"
                                />
                                <h3 className="font-display mt-5 text-lg md:text-xl font-semibold text-slate-900 tracking-tight">
                                    {title}
                                </h3>
                                <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                                    {body}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
            )}

            {/* Available Territories */}
            {!SINGLE_PRODUCT_MODE && (
            <section
                data-testid="available-territories-section"
                className="border-b border-slate-200"
            >
                <div className="max-w-6xl mx-auto px-6 md:px-12 py-20 md:py-24">
                    <div className="flex items-end justify-between flex-wrap gap-6">
                        <div className="max-w-2xl">
                            <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500">
                                Coverage Map
                            </span>
                            <h2
                                data-testid="territories-headline"
                                className="font-display mt-4 text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 leading-tight"
                            >
                                Available Territories.
                            </h2>
                        </div>
                    </div>

                    <div
                        data-testid="territories-grid"
                        className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
                    >
                        {TERRITORIES.map((t) => {
                            const isSelected = selectedTerritoryId === t.id;
                            return (
                                <button
                                    key={t.id}
                                    type="button"
                                    onClick={() => {
                                        setSelectedTerritoryId(t.id);
                                        setTimeout(() => {
                                            const el = document.getElementById(
                                                "territory-detail"
                                            );
                                            if (el) el.scrollIntoView({ block: "start", behavior: "smooth" });
                                        }, 40);
                                    }}
                                    aria-pressed={isSelected}
                                    data-testid={`territory-card-${t.id}`}
                                    className={`group text-left flex flex-col h-full border rounded-lg p-7 transition-all
                                        ${
                                            isSelected
                                                ? "border-slate-900 bg-slate-900 text-white shadow-lg shadow-slate-200/60"
                                                : t.statusTone === "active"
                                                ? "border-slate-900 bg-white hover:bg-slate-50"
                                                : "border-slate-200 bg-white hover:border-slate-400"
                                        }`}
                                >
                                    <div className="flex items-start justify-between gap-3">
                                        <h3
                                            data-testid={`territory-name-${t.id}`}
                                            className={`font-display text-xl md:text-2xl font-semibold tracking-tight ${
                                                isSelected ? "text-white" : "text-slate-900"
                                            }`}
                                        >
                                            {t.name}
                                        </h3>
                                        {isSelected ? (
                                            <span
                                                data-testid={`territory-status-${t.id}`}
                                                className="inline-flex items-center text-[11px] font-medium uppercase tracking-[0.14em] px-2.5 py-1 rounded-full border bg-white/10 text-white border-white/20"
                                            >
                                                {t.status}
                                            </span>
                                        ) : (
                                            <StatusPill
                                                tone={t.statusTone}
                                                testId={`territory-status-${t.id}`}
                                            >
                                                {t.status}
                                            </StatusPill>
                                        )}
                                    </div>

                                    <dl className="mt-6 space-y-3">
                                        <div>
                                            <dt className={`text-[11px] font-medium uppercase tracking-[0.12em] ${isSelected ? "text-slate-400" : "text-slate-500"}`}>
                                                Categories
                                            </dt>
                                            <dd className={`mt-1 text-sm leading-relaxed ${isSelected ? "text-slate-200" : "text-slate-700"}`}>
                                                {t.categories}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt className={`text-[11px] font-medium uppercase tracking-[0.12em] ${isSelected ? "text-slate-400" : "text-slate-500"}`}>
                                                Local Distribution
                                            </dt>
                                            <dd className={`mt-1 text-sm leading-relaxed ${isSelected ? "text-slate-200" : "text-slate-700"}`}>
                                                {t.distribution}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt className={`text-[11px] font-medium uppercase tracking-[0.12em] ${isSelected ? "text-slate-400" : "text-slate-500"}`}>
                                                Case Support
                                            </dt>
                                            <dd className={`mt-1 text-sm leading-relaxed ${isSelected ? "text-slate-200" : "text-slate-700"}`}>
                                                {t.caseSupport}
                                            </dd>
                                        </div>
                                    </dl>

                                    <div className={`mt-7 pt-6 border-t ${isSelected ? "border-white/10" : "border-slate-100"}`}>
                                        <span
                                            data-testid={`territory-cta-${t.id}`}
                                            className={`inline-flex items-center gap-1.5 text-sm font-medium pb-0.5 border-b ${
                                                isSelected
                                                    ? "text-white border-white"
                                                    : "text-slate-900 border-slate-900"
                                            }`}
                                        >
                                            {isSelected ? "Selected" : "View Territory"}
                                            <ArrowRight size={14} />
                                        </span>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </section>
            )}

            <section data-testid="disclaimer-section" className="bg-[#FAFAFA]">
                <div className="max-w-6xl mx-auto px-6 md:px-12 py-14 md:py-16">
                    <div className="max-w-4xl">
                        <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-slate-500">
                            Important Disclaimer
                        </span>
                        <div className="mt-4 space-y-4 text-xs md:text-sm text-slate-500 leading-relaxed">
                            <p data-testid="disclaimer-p1">
                                Product information on 1099er is provided for
                                business-development and rep-matching purposes only.
                                1099er does not provide medical advice, clinical
                                recommendations, product training, legal advice,
                                employment advice, compliance advice, or regulatory
                                advice.
                            </p>
                            <p data-testid="disclaimer-p2">
                                Reps, distributors, companies, and manufacturers are
                                responsible for reviewing product indications, regulatory
                                status, training requirements, facility policies,
                                credentialing rules, employment agreements, non-compete
                                and non-solicit obligations, compensation arrangements,
                                local distribution responsibilities, and applicable
                                compliance requirements before entering any business
                                relationship.
                            </p>
                            <p data-testid="disclaimer-p3">
                                Product opportunities shown on 1099er should not be
                                interpreted as clinical recommendations or endorsements.
                            </p>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
